/**
 * §notif-center — header bell + history dropdown.
 *
 * Polls the unread count for the badge and the list for the panel. Opening the
 * panel marks everything read (clears the badge); each row can be opened
 * (navigates to the source) or dismissed (removed from the panel, kept as
 * history server-side). Uses GlassPopover so the dropdown renders correctly on
 * every platform.
 */

import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Bell, Check, CheckCheck, X } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GlassPopover } from "@/components/glass/GlassPopover";
import {
  listNotifications,
  notificationsUnreadCount,
  markAllNotificationsRead,
  dismissNotification,
  notificationHref,
  type AppNotification,
} from "@/lib/appNotifications";
import { notificationIcon } from "./icons";

function timeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const countQ = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: notificationsUnreadCount,
    refetchInterval: 15_000,
  });

  const listQ = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => listNotifications(50),
    refetchInterval: 20_000,
  });

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const dismiss = useMutation({
    mutationFn: dismissNotification,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unread = countQ.data?.count ?? 0;
  const items: AppNotification[] = listQ.data?.notifications ?? [];

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markAllRead.mutate();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="notif-bell-btn"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={17} strokeWidth={1.85} aria-hidden />
        {unread > 0 && (
          <span key={unread} className="notif-badge" aria-hidden>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <GlassPopover
        open={open}
        anchor={triggerRef}
        onClose={() => setOpen(false)}
        align="end"
        minWidth={372}
        ariaLabel="Notifications"
        className="notif-panel"
      >
        <div className="notif-panel-head">
          <div className="notif-panel-head-l">
            <span className="notif-panel-title">Notifications</span>
            {items.length > 0 && (
              <span className="notif-head-count">{items.length}</span>
            )}
          </div>
          {items.length > 0 && (
            <button
              type="button"
              className="notif-panel-action"
              onClick={() => markAllRead.mutate()}
            >
              <CheckCheck size={14} strokeWidth={2} aria-hidden /> Mark all read
            </button>
          )}
        </div>

        <div className="notif-panel-list">
          {items.length === 0 ? (
            <div className="notif-empty">
              <span className="notif-empty-icon" aria-hidden>
                <Check size={22} strokeWidth={2} />
              </span>
              <div className="notif-empty-title">You're all caught up</div>
              <div className="notif-empty-sub">
                New orders, tickets and alerts land here.
              </div>
            </div>
          ) : (
            items.map((n) => {
              const Icon = notificationIcon(n);
              const href = notificationHref(n);
              const go = href
                ? () => {
                    setOpen(false);
                    navigate(href);
                  }
                : undefined;
              return (
                <div
                  key={n.id}
                  className={
                    `notif-item notif-sev-${n.severity}` +
                    (n.read_at ? "" : " is-unread") +
                    (href ? " is-clickable" : "")
                  }
                  role={href ? "button" : undefined}
                  tabIndex={href ? 0 : undefined}
                  onClick={go}
                  onKeyDown={
                    go
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            go();
                          }
                        }
                      : undefined
                  }
                >
                  <span className="notif-chip" aria-hidden>
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <div className="notif-item-main">
                    <div className="notif-item-title">{n.title}</div>
                    {n.body && <div className="notif-item-body">{n.body}</div>}
                    <div className="notif-item-meta">
                      <span className="notif-item-kind">{n.kind}</span>
                      <span className="notif-dot-sep" aria-hidden>
                        •
                      </span>
                      <span>{timeAgo(n.created_at)}</span>
                    </div>
                  </div>
                  {!n.read_at && (
                    <span className="notif-item-flag" aria-hidden>
                      <span className="notif-unread-dot" />
                    </span>
                  )}
                  <button
                    type="button"
                    className="notif-item-dismiss"
                    aria-label="Dismiss"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismiss.mutate(n.id);
                    }}
                  >
                    <X size={14} aria-hidden />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </GlassPopover>
    </>
  );
}
